/**
 * 暖棕摩卡模板 - 首页
 *
 * 设计理念：咖啡馆质感 + 商务杂志目录
 * - 暖米白底大字标题 Hero（无深色块，错落叠放双功能卡）
 * - 全宽焦糖色横向滚动分类条
 * - 「时间线」样式最新信息列表（全仓唯一）
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/templates/mocha/components/Navbar";
import Footer from "@/templates/mocha/components/Footer";
import { getSiteInfo, getSiteCategories, getListings, type ListingItem, type FrontendCategory } from "@/utils/api";
import { useSite } from "@/context/SiteContext";

export default function MochaHomePage() {
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();

  const [siteTitle, setSiteTitle] = useState("");
  const [searchConfig, setSearchConfig] = useState({ enabled: true, disabledMessage: "" });
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchTip, setShowSearchTip] = useState(false);

  useEffect(() => {
    getSiteInfo().then((info) => {
      if (info) {
        document.title = info.seo_title || '';
        setSiteTitle(info.site_name || '');
        if (info.search_enabled !== undefined) {
          setSearchConfig({
            enabled: info.search_enabled !== false,
            disabledMessage: info.search_disabled_message || "搜索功能已关闭",
          });
        }
      }
    });
  }, []);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getSiteCategories()
      .then((cats) => setCategories(cats.slice(0, 10)))
      .catch(console.error)
      .finally(() => setLoadingCategories(false));
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    getListings({ page_size: 12 })
      .then((data) => setListings(data.list || []))
      .catch(console.error)
      .finally(() => setLoadingListings(false));
  }, [siteId, siteLoading]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchConfig.enabled) {
      setShowSearchTip(true);
      setTimeout(() => setShowSearchTip(false), 3000);
      return;
    }
    if (searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  return (
    <div className="min-h-screen bg-[#FAF6F0]">
      <Navbar />

      {/* ================= Hero：暖白底大字标题 + 错落双卡 ================= */}
      <section className="pt-24 md:pt-28 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">

            {/* 左：标题区（无深色背景块） */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-8 h-0.5 bg-[#C77B3F]" />
                <span className="text-xs font-bold tracking-[0.2em] text-[#C77B3F]">FIND THE RIGHT SERVICE</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-black text-[#4A3525] leading-tight tracking-tight">
                {siteTitle || "分类信息服务平台"}
                <span className="block text-[#C77B3F] mt-1">让每一条信息都有价值</span>
              </h1>
              <p className="mt-4 text-sm md:text-base text-[#8A7B6D] leading-relaxed max-w-xl">
                真实可靠的信息内容，覆盖多元服务分类。简洁高效地发布、查找你所需的信息。
              </p>

              {/* 搜索框：焦糖边框 + 深棕按钮 */}
              <form onSubmit={handleSearch} className="mt-7 max-w-xl">
                <div className="relative flex items-center border-2 border-[#C77B3F] rounded-lg overflow-hidden bg-white shadow-sm focus-within:border-[#B0672F] transition-colors">
                  <i className="ri-search-line text-[#C77B3F] pl-4" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜索感兴趣的信息..."
                    className="flex-1 py-3 px-3 text-sm text-[#2B241D] placeholder-[#B7A892] focus:outline-none"
                  />
                  <button
                    type="submit"
                    className="bg-[#4A3525] text-[#F5EBDD] px-6 py-3 text-sm font-bold hover:bg-[#3A2A1D] transition-colors cursor-pointer shrink-0"
                  >
                    搜索
                  </button>
                </div>
                {showSearchTip && (
                  <p className="mt-2 text-xs text-[#C77B3F] flex items-center gap-1">
                    <i className="ri-error-warning-line" />
                    {searchConfig.disabledMessage || "搜索功能已关闭"}
                  </p>
                )}
              </form>

              {/* 快捷分类 chips */}
              <div className="mt-5 flex flex-wrap gap-2">
                {categories.slice(0, 5).map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E8DCC8] rounded-full text-xs text-[#4A3525] hover:border-[#C77B3F] hover:text-[#C77B3F] transition-colors"
                  >
                    <i className={`${cat.icon || 'ri-folder-line'} text-[#C77B3F]`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* 右：错落叠放双卡（一高一低） */}
            <div className="lg:col-span-5">
              <div className="relative h-[340px] md:h-[380px]">
                {/* 卡1：精选分类（深棕实底，向上错位） */}
                <div className="absolute top-0 right-0 w-[78%] bg-[#4A3525] rounded-xl p-5 shadow-xl shadow-[#4A3525]/20">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-bold text-[#F5EBDD] flex items-center gap-2">
                      <i className="ri-grid-fill text-[#DCA96B]" />精选分类
                    </h3>
                    <Link to="/category" className="text-xs text-[#DCA96B] hover:text-white transition-colors">
                      全部 →
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    {categories.slice(0, 6).map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.slug}`}
                        className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-2.5 py-2 transition-colors"
                      >
                        <i className={`${cat.icon || 'ri-folder-line'} text-[#DCA96B] text-sm`} />
                        <span className="text-xs text-[#EADFCE] truncate">{cat.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* 卡2：快捷服务（焦糖渐变，向下错位） */}
                <div className="absolute bottom-0 left-0 w-[60%] bg-gradient-to-br from-[#C77B3F] to-[#A8622A] rounded-xl p-5 shadow-xl shadow-[#C77B3F]/25">
                  <h3 className="text-sm font-bold text-white flex items-center gap-2 mb-4">
                    <i className="ri-service-fill" />快捷服务
                  </h3>
                  <div className="flex flex-col gap-2.5">
                    <Link to="/publish" className="flex items-center gap-2.5 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-2.5 transition-colors">
                      <i className="ri-add-circle-line text-white text-lg" />
                      <span className="text-sm text-white font-medium">发布信息</span>
                    </Link>
                    <Link to="/vip" className="flex items-center gap-2.5 bg-white/15 hover:bg-white/25 rounded-lg px-3 py-2.5 transition-colors">
                      <i className="ri-vip-crown-line text-white text-lg" />
                      <span className="text-sm text-white font-medium">会员服务</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ================= 焦糖色横向滚动分类条 ================= */}
      <section className="pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-r from-[#C77B3F] to-[#B0672F] rounded-lg px-4 py-3">
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
              <span className="shrink-0 text-xs font-bold text-white flex items-center gap-1 mr-1">
                <i className="ri-arrow-left-right-line" />快速导航
              </span>
              {loadingCategories
                ? [...Array(8)].map((_, i) => (
                    <div key={i} className="h-8 w-20 bg-white/15 rounded-full animate-pulse shrink-0" />
                  ))
                : categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white/15 hover:bg-white text-[#4A3525] rounded-full text-xs font-medium transition-colors"
                    >
                      <i className={`${cat.icon || 'ri-folder-line'} text-[#4A3525]`} />
                      {cat.name}
                    </Link>
                  ))}
              <Link
                to="/category"
                className="shrink-0 px-3.5 py-1.5 bg-white text-[#B0672F] rounded-full text-xs font-bold hover:bg-[#FAF6F0] transition-colors"
              >
                全部分类
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ================= 时间线样式最新信息 ================= */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-black text-[#4A3525] flex items-center gap-2">
                <i className="ri-time-line text-[#C77B3F]" />最新发布
              </h2>
              <p className="text-xs text-[#8A7B6D] mt-1">按时间线浏览最新信息</p>
            </div>
            <Link
              to="/category"
              className="text-sm font-bold text-[#C77B3F] hover:text-[#B0672F] transition-colors flex items-center gap-1"
            >
              查看全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {loadingListings ? (
            <div className="relative pl-8">
              <div className="absolute left-2.5 top-1 bottom-1 w-0.5 bg-[#E8DCC8]" />
              <div className="space-y-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="relative animate-pulse">
                    <div className="absolute -left-[25px] top-5 w-3 h-3 bg-[#C77B3F] rounded-full border-2 border-white shadow" />
                    <div className="bg-white rounded-lg border border-[#F0E6D6] p-4 flex gap-4">
                      <div className="w-20 h-20 bg-[#F0E6D6] rounded-lg shrink-0" />
                      <div className="flex-1 space-y-2.5">
                        <div className="w-1/2 h-4 bg-[#F0E6D6] rounded" />
                        <div className="w-2/3 h-3 bg-[#FAF6F0] rounded" />
                        <div className="w-24 h-3 bg-[#FAF6F0] rounded" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : listings.length > 0 ? (
            <div className="relative pl-8">
              {/* 时间竖轴 */}
              <div className="absolute left-2.5 top-2 bottom-2 w-0.5 bg-gradient-to-b from-[#C77B3F] via-[#E8DCC8] to-transparent" />
              <div className="space-y-4">
                {listings.map((listing) => (
                  <div key={listing.id} className="relative group">
                    {/* 时间圆点 */}
                    <div className="absolute -left-[25px] top-5 w-3 h-3 bg-[#C77B3F] rounded-full border-2 border-white shadow group-hover:scale-125 transition-transform" />
                    <Link
                      to={`/listing/${listing.id}`}
                      className="block bg-white rounded-lg border border-[#F0E6D6] hover:border-[#C77B3F]/50 hover:shadow-md transition-all p-4"
                    >
                      <div className="flex items-start gap-4">
                        {/* 缩略图：aspect-square */}
                        <div className="w-20 h-20 shrink-0 rounded-lg overflow-hidden bg-[#FAF6F0] flex items-center justify-center">
                          {listing.images?.[0] ? (
                            <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <i className="ri-image-line text-[#DCCFB8] text-2xl" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {listing.is_pinned && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-[#C77B3F] text-white rounded font-bold shrink-0">顶</span>
                            )}
                            <h3 className="text-sm font-bold text-[#2B241D] group-hover:text-[#C77B3F] transition-colors truncate">
                              {listing.title}
                            </h3>
                          </div>
                          <p className="text-xs text-[#8A7B6D] mt-1.5 line-clamp-1">
                            {listing.content?.substring(0, 80) || "暂无描述"}
                          </p>
                          <div className="flex items-center gap-2 mt-2.5 text-[11px] text-[#8A7B6D]">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FAF6F0] text-[#B0672F] rounded-full">
                              <i className={`${listing.category?.icon || 'ri-folder-line'} text-[10px]`} />
                              {listing.category?.name || "未分类"}
                            </span>
                            <span>·</span>
                            <span className="inline-flex items-center gap-1">
                              <i className="ri-eye-line" />{listing.view_count || 0}
                            </span>
                            <span className="ml-auto inline-flex items-center gap-1 text-[#B7A892]">
                              <i className="ri-time-line" />{formatTimeAgo(listing.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-lg border border-[#F0E6D6]">
              <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-[#FAF6F0]">
                <i className="ri-inbox-line text-xl text-[#C77B3F]" />
              </div>
              <p className="text-[#4A3525] font-medium">暂无信息</p>
              <p className="text-[#8A7B6D] text-sm mt-1">快来发布第一条信息吧</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function formatTimeAgo(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}
